<template>
  <!-- One number per book: when it moved off our frozen line, how often the
       game went its way -- with its likely range, so a gap that is still noise
       reads as noise. Season only: one week is a handful of games. -->
  <section class="card">
    <div class="flex items-start justify-between gap-2.5">
      <div>
        <h2 class="text-[15px] font-extrabold text-white">Which books to trust</h2>
        <p class="mt-1 text-[11px] leading-4 text-slate-400">When a book moved off our line, how often the game went its way. Ranked best first.</p>
      </div>
      <span v-if="trust.games" class="shrink-0 rounded-full bg-slate-400/15 px-2.5 py-1 text-[10px] font-bold text-slate-400">{{ trust.games }} games</span>
    </div>

    <template v-if="trust.rows.length">
      <div class="trust-grid mt-3 text-[9px] font-extrabold tracking-[0.12em] text-slate-500">
        <span>BOOK</span>
        <span class="flex justify-between"><span>25%</span><span>50%</span><span>75%</span></span>
        <span class="text-right">RIGHT</span>
      </div>
      <div v-for="row in trust.rows" :key="row.bookmaker" class="trust-grid min-h-[34px] items-center border-b border-white/[0.06]">
        <span class="truncate text-[13px] font-extrabold text-white">{{ label(row.bookmaker) }}</span>
        <span class="relative h-[18px]" role="img" :aria-label="`${label(row.bookmaker)}: ${pct(row.rate)} right, likely ${pct(row.low)} to ${pct(row.high)}`">
          <span class="absolute inset-y-0 left-1/2 border-l border-dashed border-white/25"></span>
          <span class="absolute top-[7px] h-1 rounded-sm bg-slate-400/35" :style="{ left: x(row.low), width: width(row) }"></span>
          <span class="absolute top-1 -ml-[5px] h-2.5 w-2.5 rounded-full" :class="row.sharp ? 'bg-sky-400' : 'bg-violet-400'" :style="{ left: x(row.rate) }"></span>
        </span>
        <span class="whitespace-nowrap text-right text-xs font-extrabold tabular-nums text-white">{{ pct(row.rate) }} <span class="text-[10px] font-semibold text-slate-400">{{ row.right }}/{{ row.counted }}</span></span>
      </div>
      <div class="mt-2.5 flex flex-wrap gap-x-3.5 gap-y-1 text-[10px] text-slate-400">
        <span><span class="mr-1 inline-block h-2 w-2 rounded-full bg-sky-400"></span>Sharp / exchange</span>
        <span><span class="mr-1 inline-block h-2 w-2 rounded-full bg-violet-400"></span>Retail</span>
        <span><span class="mr-1 inline-block h-1 w-3.5 rounded-sm bg-slate-400/35 align-middle"></span>Likely range</span>
      </div>
      <p v-if="trust.tooEarly" class="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs leading-[17px] text-amber-200">
        <b>Too early to call.</b> Every book's range still crosses 50%, so {{ label(last.bookmaker) }} at the bottom is not yet evidence it's worse.
      </p>
    </template>
    <p v-else-if="!trust.games" class="py-6 text-center text-xs text-slate-500">No frozen lines on file for this season, so there is nothing to grade the books against.</p>
    <p v-else class="py-6 text-center text-xs text-slate-500">No book has {{ minGames }}+ graded moves yet.</p>
  </section>
</template>

<script>
import { BOOKMAKER_NAMES } from "@/utils/bookmakerNames.js";
import { MIN_COUNTED_GAMES } from "@/utils/bookScorecard.js";
import { bookTrust } from "@/utils/bookTrust.js";

// The axis: 25% at the left edge, 75% at the right.
const AXIS_LOW = 0.25;
const AXIS_SPAN = 0.5;

export default {
  name: "BookTrust",
  props: {
    // `bookScorecard(games)` for the season.
    summary: { type: Object, required: true },
  },
  computed: {
    trust() {
      return bookTrust(this.summary);
    },
    last() {
      return this.trust.rows[this.trust.rows.length - 1];
    },
    minGames() {
      return MIN_COUNTED_GAMES;
    },
  },
  methods: {
    label(key) {
      return BOOKMAKER_NAMES[key] || key;
    },
    pct(value) {
      return `${Math.round(value * 100)}%`;
    },
    clamp(value) {
      return Math.max(0, Math.min(100, ((value - AXIS_LOW) / AXIS_SPAN) * 100));
    },
    x(value) {
      return `${this.clamp(value).toFixed(1)}%`;
    },
    width(row) {
      return `${(this.clamp(row.high) - this.clamp(row.low)).toFixed(1)}%`;
    },
  },
};
</script>

<style scoped>
.card { border: 1px solid rgb(255 255 255 / 0.1); border-radius: 1.25rem; background: rgb(1 16 38 / 0.45); padding: 1rem 0.875rem 0.875rem; }
.trust-grid { display: grid; grid-template-columns: 5.75rem minmax(0, 1fr) 4.25rem; gap: 0.5rem; }
</style>
